import React, { useState } from "react";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Tooltip from "@mui/material/Tooltip";
import {
  Card,
  CardImage,
  Title,
  Subtitle,
  ConditionBadge,
  SKUText,
  MetaRow,
  Price,
  CardFooter,
  ButtonBlue,
  LocationPill,
  InfoText
} from "./ProductCard.styles";
import ProductInfo from "../ProductInfo/ProductInfo";
import { addProductToCart } from "../../cart/cartBridge";

export default function ProductCard({ product, onAddToCart }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!product) return <Card style={{ height: 360 }} />;

  const {
    id,
    brand,
    vendor,
    name,
    description,
    sku,
    current_price,
    stock_quantity,
    location,
    condition,
    is_available,
    image_url,
    image_urls,
    weight,
    dimensions,
    specifications,
    pricing_mode,
  } = product;

  console.log('ProductCard - Product data:', {
    id,
    brand,
    vendor,
    sku,
    weight,
    dimensions,
    location,
    image_url,
    image_urls,
    image_urlsType: typeof image_urls,
    image_urlsIsArray: Array.isArray(image_urls)
  });

  // Check if price is on demand
  const isPriceOnDemand = pricing_mode === 'ON_DEMAND' || current_price === null || current_price === undefined || current_price === 0;
  const price = Number(current_price || 0);
  const available = Number(stock_quantity || 0);
  const titleText = description || `${brand || ""} ${name || ""}`;

  const getValidImages = () => {
    const validImages = [];

    // Check image_urls array first
    if (image_urls) {
      // Handle both array and string cases
      if (Array.isArray(image_urls)) {
        const validFromArray = image_urls.filter(
          (url) => url && url.trim() && !url.includes('""') && url !== 'null' && url !== 'undefined' && url !== 'None'
        );
        validImages.push(...validFromArray);
      } else if (typeof image_urls === 'string' && image_urls.trim()) {
        // If it's a string, try to parse it (might be JSON string) or use it directly
        try {
          const parsed = JSON.parse(image_urls);
          if (Array.isArray(parsed)) {
            const validFromParsed = parsed.filter(
              (url) => url && url.trim() && !url.includes('""') && url !== 'null' && url !== 'undefined' && url !== 'None'
            );
            validImages.push(...validFromParsed);
          } else {
            validImages.push(image_urls.trim());
          }
        } catch {
          // Not JSON, use as-is
          validImages.push(image_urls.trim());
        }
      }
    }

    // Check single image_url
    if (image_url && image_url.trim() && image_url !== 'null' && image_url !== 'undefined' && image_url !== 'None') {
      // Avoid duplicates
      if (!validImages.includes(image_url.trim())) {
        validImages.push(image_url.trim());
      }
    }

    if (validImages.length === 0) {
      return ["/ProductHolder.png"];
    }

    return validImages;
  };

  const getOverlayImage = () => {
    if (!condition) return null;

    const isLenovoVendor = vendor?.toLowerCase().includes("lenovo");
    const isLCRVendor = vendor?.toLowerCase() === "lcr";

    if (condition.toLowerCase() === "new") {
      return isLenovoVendor ? "/LEN-New.png" : "/GEN-New.png";
    }

    if (condition.toLowerCase().startsWith("refurb")) {
      return (isLenovoVendor || isLCRVendor) ? "/LEN-Refurbs.png" : "/GEN-Refurbs.png";
    }

    return null;
  };

  const overlayImage = getOverlayImage();
  const validImages = getValidImages();
  console.log('ProductCard - Valid images:', validImages, 'Current index:', currentImageIndex);
  const currentImage = validImages[currentImageIndex] || "/ProductHolder.png";
  const hasMultipleImages = validImages.length > 1;

  const goToPreviousImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? validImages.length - 1 : prev - 1
    );
  };

  const goToNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === validImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleCardClick = (e) => {
    if (e.target.closest("button")) return;
    setOpen(true);
  };

  const handleAdd = async () => {
    try {
      setAdding(true);
      await addProductToCart(product, 1);
      onAddToCart?.(id);
      window.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { action: "add", productId: id, qty: 1 },
        })
      );
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <Card onClick={handleCardClick}>
        <div
          style={{
            position: "relative",
            width: "100%",
            height: 180,
            marginBottom: 10,
          }}
        >
          <CardImage
            src={currentImage}
            alt={titleText}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/ProductHolder.png";
            }}
          />

          {overlayImage && (
            <img
              src={overlayImage}
              alt="Condition Badge"
              style={{
                position: "absolute",
                top: 0,
                left: 8,
                width: 60,
                height: 60,
                objectFit: "contain",
                zIndex: 3,
              }}
            />
          )}

          {hasMultipleImages && (
            <>
              <button
                onClick={goToPreviousImage}
                style={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0,0,0,0.5)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 2,
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </button>

              <button
                onClick={goToNextImage}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0,0,0,0.5)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 2,
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </button>

              <div
                style={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  background: "rgba(0,0,0,0.7)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: 12,
                  fontSize: 12,
                  zIndex: 2,
                }}
              >
                {currentImageIndex + 1} / {validImages.length}
              </div>
            </>
          )}
        </div>

        <Tooltip title={titleText} arrow placement="top">
          <Title variant="subtitle1">{titleText}</Title>
        </Tooltip>
        <Subtitle>
          {brand && <span>{brand}</span>}{" "}
          {condition && <ConditionBadge>{condition}</ConditionBadge>}
        </Subtitle>

        <SKUText>SKU: {sku || "-"}</SKUText>

        <MetaRow>
          <Inventory2OutlinedIcon fontSize="small" />
          <InfoText>Stock: {available}</InfoText>
        </MetaRow>

        <Price variant="h6">
          {isPriceOnDemand ? "Price on Demand" : `$${price.toFixed(2)} (VAT excl.)`}
        </Price>

        <CardFooter>
          <LocationPill>
            <PlaceOutlinedIcon fontSize="small" />
            <span>{location || "—"}</span>
          </LocationPill>

          <ButtonBlue
            variant="contained"
            size="small"
            disabled={available === 0 || adding}
            onClick={handleAdd}
          >
            {adding ? "Adding…" : "Add to Cart"}
          </ButtonBlue>
        </CardFooter>
      </Card>

      {open && (
        <ProductInfo
          id={id}
          title={titleText}
          brand={brand}
          description={description}
          sku={sku}
          condition={condition}
          availability={is_available ? "In Stock" : "Out of Stock"}
          price={current_price}
          image={currentImage}
          weight={weight}
          dimensions={dimensions}
          stock_quantity={stock_quantity}
          location={location}
          pricing_mode={pricing_mode}
          vendor={vendor}
          specifications={specifications}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
